import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from 'react-query';
import { AircraftsAPI, AircraftCategory } from '../services/aircrafts.ts';
import api from '../services/api.ts';
import { applyCurrencyMask, parseCurrency } from '../utils/currency.ts';
import { ArrowLeftIcon, DocumentTextIcon, PaperAirplaneIcon } from '@heroicons/react/24/outline';
import FileUpload, { FileAttachment } from '../components/FileUpload.tsx';

interface AircraftFormData {
  acquisitionValue: number;
  currentValue: number;
  acquisitionDate: string;
  category: AircraftCategory;
  attachments: FileAttachment[];
  details: {
    make: string;
    model: string;
    year: number;
    tailNumber: string;
    color: string;
    notes?: string;
  };
}

const CreateAircraft: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState<AircraftFormData>({
    acquisitionValue: 0,
    currentValue: 0,
    acquisitionDate: new Date().toISOString().split('T')[0],
    category: 'airplane',
    attachments: [],
    details: {
      make: '',
      model: '',
      year: new Date().getFullYear(),
      tailNumber: '',
      color: '',
    },
  });

  const [acquisitionValueDisplay, setAcquisitionValueDisplay] = useState<string>('');
  const [currentValueDisplay, setCurrentValueDisplay] = useState<string>('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const createMutation = useMutation(
    async (data: AircraftFormData) => {
      let uploadedFiles: any[] = [];
      
      // Primeiro, fazer upload dos arquivos se existirem
      if (data.attachments && data.attachments.length > 0) {
        const formData = new FormData();
        data.attachments.forEach((attachment: any) => {
          formData.append('files', attachment.file);
        });

        const uploadResponse = await api.post('/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        if (uploadResponse.data.success) {
          uploadedFiles = uploadResponse.data.data.files.map((file: any, index: number) => ({
            id: Date.now() + index,
            name: file.originalName,
            filename: file.filename,
            url: file.url,
            size: file.size,
            mimetype: file.mimetype,
            type: data.attachments[index].type,
            category: data.attachments[index].category,
          }));
        }
      }

      const payload = {
        make: data.details.make,
        model: data.details.model,
        year: data.details.year,
        tailNumber: data.details.tailNumber,
        color: data.details.color,
        purchasePrice: data.acquisitionValue,
        sellingPrice: data.currentValue || null,
        category: data.category,
        purchaseDate: data.acquisitionDate,
        notes: data.details.notes || undefined,
        attachments: uploadedFiles,
      };
      return AircraftsAPI.create(payload);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('aircrafts');
        navigate('/aircrafts');
      },
      onError: (error: any) => {
        console.error('Erro ao criar aeronave:', error);
      },
    }
  );

  const handleCurrencyChange = (field: 'acquisitionValue' | 'currentValue', value: string) => {
    const masked = applyCurrencyMask(value);
    const numeric = parseCurrency(masked);
    if (field === 'acquisitionValue') setAcquisitionValueDisplay(masked);
    else setCurrentValueDisplay(masked);
    setFormData(prev => ({ ...prev, [field]: numeric }));
    if (errors[field]) {
      setErrors(prev => {
        const ne = { ...prev };
        delete ne[field];
        return ne;
      });
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handleDetailsChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, details: { ...prev.details, [field]: value } }));
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (formData.acquisitionValue <= 0) newErrors.acquisitionValue = 'Valor de aquisição inválido';
    // if (formData.currentValue <= 0) newErrors.currentValue = 'Valor de venda inválido';
    if (!formData.acquisitionDate) newErrors.acquisitionDate = 'Data de aquisição é obrigatória';
    if (!formData.category) newErrors.category = 'Categoria é obrigatória';
    if (!formData.details.make?.trim()) newErrors.make = 'Marca é obrigatória';
    if (!formData.details.model?.trim()) newErrors.model = 'Modelo é obrigatório';
    if (!formData.details.year || formData.details.year < 1900) newErrors.year = 'Ano inválido';
    if (!formData.details.tailNumber?.trim()) newErrors.tailNumber = 'Prefixo é obrigatório';
    if (!formData.details.color?.trim()) newErrors.color = 'Cor é obrigatória';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) createMutation.mutate(formData);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <button
            onClick={() => navigate('/aircrafts')}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4 transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Voltar para Aeronaves
          </button>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <PaperAirplaneIcon className="h-8 w-8 mr-3 text-primary-600" />
            Criar Nova Aeronave
          </h1>
          <p className="text-gray-600 mt-2">Adicione uma nova aeronave ao seu patrimônio</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
              <DocumentTextIcon className="h-6 w-6 mr-2 text-blue-600" />
              Informações Básicas
            </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-2">Categoria *</label>
                <select
                  id="category"
                  value={formData.category}
                  onChange={e => handleInputChange('category', e.target.value as AircraftCategory)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.category ? 'border-red-500' : 'border-gray-300'}`}
                >
                  <option value="airplane">Avião</option>
                  <option value="helicopter">Helicóptero</option>
                  <option value="other">Outros</option>
                </select>
              </div>
              <div>
                <label htmlFor="acquisitionDate" className="block text-sm font-medium text-gray-700 mb-2">Data de Aquisição *</label>
                <input
                  id="acquisitionDate"
                  type="date"
                  value={formData.acquisitionDate}
                  onChange={e => handleInputChange('acquisitionDate', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.acquisitionDate ? 'border-red-500' : 'border-gray-300'}`}
                />
              </div>

              <div>
                <label htmlFor="make" className="block text-sm font-medium text-gray-700 mb-2">Marca *</label>
                <input
                  id="make"
                  type="text"
                  value={formData.details.make}
                  onChange={e => handleDetailsChange('make', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.make ? 'border-red-500' : 'border-gray-300'}`}
                />
              </div>

              <div>
                <label htmlFor="model" className="block text-sm font-medium text-gray-700 mb-2">Modelo *</label>
                <input
                  id="model"
                  type="text"
                  value={formData.details.model}
                  onChange={e => handleDetailsChange('model', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.model ? 'border-red-500' : 'border-gray-300'}`}
                />
              </div>

              <div>
                <label htmlFor="year" className="block text-sm font-medium text-gray-700 mb-2">Ano *</label>
                <input
                  id="year"
                  type="number"
                  value={formData.details.year}
                  onChange={e => handleDetailsChange('year', Number(e.target.value))}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.year ? 'border-red-500' : 'border-gray-300'}`}
                />
              </div>

              <div>
                <label htmlFor="tailNumber" className="block text-sm font-medium text-gray-700 mb-2">Prefixo *</label>
                <input
                  id="tailNumber"
                  type="text"
                  value={formData.details.tailNumber}
                  onChange={e => handleDetailsChange('tailNumber', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.tailNumber ? 'border-red-500' : 'border-gray-300'}`}
                />
              </div>

              <div>
                <label htmlFor="color" className="block text-sm font-medium text-gray-700 mb-2">Cor *</label>
                <input
                  id="color"
                  type="text"
                  value={formData.details.color}
                  onChange={e => handleDetailsChange('color', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.color ? 'border-red-500' : 'border-gray-300'}`}
                />
              </div>
              <div className="md:col-span-2">
                <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-2">Observação</label>
                <textarea
                  id="notes"
                  value={formData.details.notes || ''}
                  onChange={e => handleDetailsChange('notes', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent border-gray-300"
                  placeholder="Anotações sobre a aeronave"
                />
              </div>
            </div>
          </div>

          {/* Anexos - Fotos e Documentos */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center mb-4">
              <DocumentTextIcon className="h-6 w-6 text-blue-600 mr-2" />
              <h3 className="text-lg font-semibold text-gray-900">Fotos e Documentos</h3>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Adicione fotos da aeronave e documentos como certificado de aeronavegabilidade, manual, etc.
            </p>
            <FileUpload
              files={formData.attachments}
              onFilesChange={files => setFormData(prev => ({ ...prev, attachments: files }))}
              maxFiles={20}
              maxSizePerFile={10}
              showCategories={true}
            />
          </div>

          {/* Valores */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-6">Valores</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="acquisitionValue" className="block text-sm font-medium text-gray-700 mb-2">Valor de Aquisição *</label>
                <input
                  id="acquisitionValue"
                  type="text"
                  value={acquisitionValueDisplay}
                  onChange={e => handleCurrencyChange('acquisitionValue', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.acquisitionValue ? 'border-red-500' : 'border-gray-300'}`}
                  placeholder="R$ 0,00"
                />
              </div>

              <div>
                <label htmlFor="currentValue" className="block text-sm font-medium text-gray-700 mb-2">Valor de Venda</label>
                <input
                  id="currentValue"
                  type="text"
                  value={currentValueDisplay}
                  onChange={e => handleCurrencyChange('currentValue', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.currentValue ? 'border-red-500' : 'border-gray-300'}`}
                  placeholder="R$ 0,00"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg transition-colors"
            >
              Criar Aeronave
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateAircraft;
